import { useEffect, useRef } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailFields,
  MiFileUpload,
  MiLoader,
} from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  HTTP_STATUSES,
  INPUT_TYPES,
  ISSUE_RELATIONSHIPS,
  ISSUE_TYPES,
} from "@miplatform/constants";
import { useComponentState, useEdit } from "@miplatform/hooks";
import { createUUID, externalReferenceId } from "@miplatform/utils";
import { Box } from "@mui/material";
import { AreaAutoComplete } from "../../../../../../../components/AreaAutoComplete/AreaAutoComplete";
import { useIssueEditorViewModel } from "./useIssueEditorViewModel";

const INITIAL_EDIT = {
  issueTemplateId: "",
  issueNameId: "",
  areaName: "",
  description: "",
};

export function IssueEditor({
  assignmentId = "",
  orderTemplateId = null,
  orderId,
  getAttempt = () => {},
  onCancel = () => {},
}) {
  const ref = useRef();
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const {
    createIssue,
    getAssignmentTemplates,
    issueTemplates,
    setDocuments,
    saveDocuments,
  } = useIssueEditorViewModel({
    orderTemplateId,
  });

  useEffect(() => {
    setTimeout(() => {
      ref.current.scrollIntoView({ behavior: "smooth" });
    }, 300);
    getAssignmentTemplates();
  }, []);

  const issueFields = [
    {
      label: "Task Title",
      fieldType: INPUT_TYPES.SELECT,
      name: "issueTemplateId",
      width: "100%",
      handleChange: (e) => {
        const issueSelected = issueTemplates.find(
          (x) => x.id === e.target.value
        );
        edit.update({
          issueTemplateId: issueSelected.id,
          issueTemplateName: issueSelected.name,
          type: issueSelected.recordType,
        });
      },
      options: issueTemplates.map((x) => ({ label: x.name, value: x.id })),
      required: true,
      style: { paddingLeft: 0, paddingRight: 0 },
    },
    {
      label: "Area",
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <AreaAutoComplete
          onChange={(value) => {
            edit.update({ areaName: value });
          }}
        />
      ),
      name: "areaName",
      width: "100%",
      required: true,
      style: { paddingLeft: 0, paddingRight: 0 },
    },
    {
      label: "Description",
      name: "description",
      multiline: true,
      required: true,
      rows: 3,
      width: "100%",
      style: { paddingLeft: 0, paddingRight: 0 },
    },
  ];

  const edit = useEdit(INITIAL_EDIT, issueFields);

  const { classes } = useStyles();

  const handleSubmit = async () => {
    const issueId = createUUID();
    const { status } = await createIssue({
      id: issueId,
      originalDescription: edit.edits.description,
      areaName: edit.edits.areaName,
      title: edit.edits.issueTemplateName,
      issueTemplateId: edit.edits.issueTemplateId,
      description: "",
      type: edit.edits.type || ISSUE_TYPES.RECORDABLE,
      assignmentId,
      relationships: [
        {
          id: createUUID(),
          externalReferenceId: externalReferenceId.getAssignmentRefId({
            assignmentId,
            orderId,
          }),
          relationship: ISSUE_RELATIONSHIPS.RESOLVED_BY,
        },
      ],
    });

    if (status === HTTP_STATUSES.OK) {
      await saveDocuments(issueId);
      getAttempt();
      onCancel();
    }
  };

  if (isLoading) {
    return <MiLoader />;
  }

  return (
    <Box width="100%">
      <MiDetailFields detailFields={issueFields} edit={edit} />
      <MiFileUpload
        dragAndDrop
        onChange={setDocuments}
        testId="issue-document"
      />
      <Box className={classes.footer} ref={ref}>
        <MiButton
          className={classes.cancelBtn}
          title="Cancel"
          variant={BUTTON_VARIANTS.TEXT}
          onClick={onCancel}
        />
        <MiButton
          className={classes.confirmBtn}
          title="Create Task"
          variant={BUTTON_VARIANTS.CONTAINED}
          disabled={
            !edit.allFilled("issueTemplateName", "areaName", "description")
          }
          onClick={handleSubmit}
        />
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  footer: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(4),
    paddingTop: theme.spacing(1),
  },
  cancelBtn: {
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
  },
  confirmBtn: {
    color: theme.palette.common.white,
    fontWeight: theme.typography.fontWeightBold,
  },
}));
